/**
 * In-App Tab Navigation — shared vocabulary for the tab bar.
 *
 * Everything that needs to know what a tab *is* reads from here: the tab bar, the record links,
 * the tab host, and the permission check that guards opening one.
 */

export enum TabRecordType {
  QUOTATION = 'QUOTATION',
  JOB = 'JOB',
  CLIENT = 'CLIENT',
  INVOICE = 'INVOICE'
}

/** A record tab points at one record; the pinned tab points at the section list view. */
export type TabType = TabRecordType | 'LIST';

/** The sections that own a tab bar. Others (Analytics, Equipments, Settings) do not. */
export enum TabSection {
  QUOTATIONS = 'QUOTATIONS',
  JOBS = 'JOBS',
  CLIENTS = 'CLIENTS',
  INVOICES = 'INVOICES',
  SCHEDULE = 'SCHEDULE'
}

/** Total tabs held in memory at once, pinned tab included. See TAB-08 and the 12 tab decision. */
export const MAX_TABS = 12;

/** Client names truncate; record id labels (QT #, Job #, INV #) are short by nature and do not. */
export const CLIENT_LABEL_MAX_LENGTH = 20;

/** Route prefix per record type — also the section list path. */
export const TAB_RECORD_PATH: Record<TabRecordType, string> = {
  [TabRecordType.QUOTATION]: '/quotations',
  [TabRecordType.JOB]: '/jobs',
  [TabRecordType.CLIENT]: '/clients',
  [TabRecordType.INVOICE]: '/invoices'
};

export const TAB_SECTION_PATH: Record<TabSection, string> = {
  [TabSection.QUOTATIONS]: '/quotations',
  [TabSection.JOBS]: '/jobs',
  [TabSection.CLIENTS]: '/clients',
  [TabSection.INVOICES]: '/invoices',
  [TabSection.SCHEDULE]: '/schedule'
};

export const TAB_SECTION_LABEL: Record<TabSection, string> = {
  [TabSection.QUOTATIONS]: 'Quotations',
  [TabSection.JOBS]: 'Jobs',
  [TabSection.CLIENTS]: 'Clients',
  [TabSection.INVOICES]: 'Invoices',
  [TabSection.SCHEDULE]: 'Schedule'
};

/**
 * Permission module per record type, matching the `module` prop already passed to ConditionalRoute
 * in App.tsx. Opening a tab must check the same module the route would have checked.
 */
export const TAB_RECORD_MODULE: Record<TabRecordType, string> = {
  [TabRecordType.QUOTATION]: 'QUOTATIONS',
  [TabRecordType.JOB]: 'JOBS',
  [TabRecordType.CLIENT]: 'CLIENTS',
  [TabRecordType.INVOICE]: 'INVOICES'
};

/** The section a record belongs to, used when a record is opened from somewhere without a tab bar. */
export const TAB_RECORD_SECTION: Record<TabRecordType, TabSection> = {
  [TabRecordType.QUOTATION]: TabSection.QUOTATIONS,
  [TabRecordType.JOB]: TabSection.JOBS,
  [TabRecordType.CLIENT]: TabSection.CLIENTS,
  [TabRecordType.INVOICE]: TabSection.INVOICES
};

/** Matches the `module` prop on each ConditionalRoute in App.tsx — note SCHEDULES is plural there. */
export const TAB_SECTION_MODULE: Record<TabSection, string> = {
  [TabSection.QUOTATIONS]: 'QUOTATIONS',
  [TabSection.JOBS]: 'JOBS',
  [TabSection.CLIENTS]: 'CLIENTS',
  [TabSection.INVOICES]: 'INVOICES',
  [TabSection.SCHEDULE]: 'SCHEDULES'
};

/**
 * Sections whose content area is owned by TabHost, and the record types allowed to become tabs.
 *
 * Both lists grow one module at a time. Anything not listed keeps the behaviour it has today, which
 * is what lets the rollout happen module by module instead of in one jump.
 */
export const TAB_HOST_SECTIONS: TabSection[] = [TabSection.QUOTATIONS, TabSection.JOBS, TabSection.CLIENTS, TabSection.INVOICES, TabSection.SCHEDULE];

export const TAB_HOST_RECORD_TYPES: TabRecordType[] = [TabRecordType.QUOTATION, TabRecordType.JOB, TabRecordType.CLIENT, TabRecordType.INVOICE];

export const isTabHostSection = (section?: TabSection): boolean => !!section && TAB_HOST_SECTIONS.includes(section);

export const isTabHostRecordType = (recordType: TabRecordType): boolean => TAB_HOST_RECORD_TYPES.includes(recordType);

export const buildRecordPath = (recordType: TabRecordType, recordId: string | number): string => `${TAB_RECORD_PATH[recordType]}/${recordId}`;

/** The record a pathname points at, when it points at one this feature knows how to host. */
export const matchRecordPath = (pathname: string): { recordType: TabRecordType; recordId: string } | undefined => {
  const recordTypes = Object.keys(TAB_RECORD_PATH) as TabRecordType[];

  return recordTypes
    .map(recordType => {
      const prefix = `${TAB_RECORD_PATH[recordType]}/`;
      if (!pathname.startsWith(prefix)) {
        return undefined;
      }

      const recordId = pathname.slice(prefix.length).replace(/\/$/, '');
      return recordId && !recordId.includes('/') ? { recordType, recordId } : undefined;
    })
    .find(Boolean);
};

/** Identity of a tab: one record of one type may only ever have one tab open (TAB-05). */
export const buildTabKey = (type: TabType, recordId?: string | number): string => {
  return type === 'LIST' ? `LIST:${recordId}` : `${type}:${recordId}`;
};

export const truncateTabLabel = (label: string, maxLength: number = CLIENT_LABEL_MAX_LENGTH): string => {
  // Belt and braces: the label comes from record data, and a tab caption is cosmetic — it must never
  // be the thing that takes down a click. A list endpoint returning an aggregate where the model
  // promised a string is exactly how that happened once.
  const trimmed = String(label).trim();
  // The cut often lands on a space, and "Acme Industrial Pte …" reads as a gap rather than a name.
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength).replace(/\s+$/, '')}…` : trimmed;
};

/**
 * Tab label per record type. Client names truncate at 20 characters; the rest are short ids.
 * `fallbackLabel` is the record name supplied by the link that opened the tab.
 */
export const buildTabLabel = (type: TabRecordType, recordId: string | number, fallbackLabel?: string): string => {
  switch (type) {
    case TabRecordType.QUOTATION:
      return `QT #${recordId}`;
    case TabRecordType.JOB:
      return `Job #${recordId}`;
    case TabRecordType.INVOICE:
      return `INV #${recordId}`;
    case TabRecordType.CLIENT:
      return truncateTabLabel(fallbackLabel || `Client #${recordId}`);
    default:
      return String(recordId);
  }
};

/**
 * Which section a pathname belongs to, or undefined for routes without a tab bar
 * (Schedule, Analytics, Equipments, Settings, login, and everything else).
 */
export const getSectionFromPath = (pathname: string): TabSection | undefined => {
  const sections = Object.keys(TAB_SECTION_PATH) as TabSection[];
  return sections.find(section => {
    const path = TAB_SECTION_PATH[section];
    return pathname === path || pathname.startsWith(`${path}/`);
  });
};
